import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LogoutReason, LOGOUT_RETURN_URL_KEY } from './auth/logout.service';

export interface LoginMessage {
  type: 'LOGIN_SUCCESS';
  timestamp: number;
}

export interface LogoutMessage {
  type: 'LOGOUT_INITIATED';
  timestamp: number;
  reason: LogoutReason;
  returnUrl: string | null;
}

type AuthMessage = LoginMessage | LogoutMessage;

@Injectable({
  providedIn: 'root'
})
export class BroadcastChannelService implements OnDestroy {
  #channel: BroadcastChannel | null = null;

  #loginSuccessSubject = new BehaviorSubject<LoginMessage | null>(null);
  public loginSuccess$: Observable<LoginMessage | null> = this.#loginSuccessSubject.asObservable();

  #logoutInitiatedSubject = new BehaviorSubject<LogoutMessage | null>(null);
  public logoutInitiated$: Observable<LogoutMessage | null> = this.#logoutInitiatedSubject.asObservable();

  constructor() {
    this.initializeBroadcastChannel();
  }

  /** Initialize the broadcast channel for cross-tab communication */
  private initializeBroadcastChannel(): void {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.#channel = new BroadcastChannel('app-auth-channel');
        
        this.listenForMessages();
      }
    } catch (error) {
      console.warn('BroadcastChannel is not supported in this browser', error);
    }
  }

  /** Listen for auth messages from other tabs and emit to subjects */
  private listenForMessages(): void {
    if (this.#channel) {
      this.#channel.onmessage = (event: MessageEvent<AuthMessage>) => {
        if (event.data.type === 'LOGIN_SUCCESS') {
          this.#loginSuccessSubject.next(event.data);
          return;
        }

        if (event.data.type === 'LOGOUT_INITIATED') {
          this.#logoutInitiatedSubject.next(event.data);
        }
      };
    }
  }

  /** Send a login success notification to all tabs */
  public notifyLoginSuccess(): void {
    if (this.#channel) {
      const message: LoginMessage = {
        type: 'LOGIN_SUCCESS',
        timestamp: Date.now()
      };
      this.#channel.postMessage(message);
    }
  }

  /** Notify all tabs that logout has started and share the route to restore later */
  public notifyLogoutInitiated(reason: LogoutReason, returnUrl: string | null): void {
    if (this.#channel) {
      const message: LogoutMessage = {
        type: 'LOGOUT_INITIATED',
        timestamp: Date.now(),
        reason,
        returnUrl
      };
      this.#channel.postMessage(message);
    }
  }

  /** Close the broadcast channel when the service is destroyed */
  ngOnDestroy(): void {
    if (this.#channel) this.#channel.close();
  }
}
